import React, { Component, createRef } from 'react';

import Form from '@rjsf/mui';
import validator from '@rjsf/validator-ajv8';
import { withSnackbar } from 'notistack';

import { Box, Button, Dialog, DialogContent, DialogTitle, Stack } from '@mui/material';

import { categories } from '@luxuryescapes/lib-global';

import { OFFER_TYPE_MAP } from '~/components/Accommodation/constants';
import Spinner from '~/components/Common/Spinner';

import { PROPERTY_CHANNEL_MANAGERS } from '~/consts/reservation';

import { getRoomImagesByInternalService, syncRoomAmenities } from '~/services/AccommodationService';
import OffersService from '~/services/OffersService';
import ReservationService from '~/services/ReservationService';

import {
  SYNC_ROOM_FETCH_LIMIT,
  SYNC_TOP_ROOM_IMAGE_COUNT,
  isPropertyNotMappedError,
  resolveCanonicalRootIds,
} from '~/utils/accommodationPropertyImageSync';
import { allSettledWithConcurrency } from '~/utils/allSettledWithConcurrency';
import featureToggle from '~/utils/featureToggle';
import presentRoomType from '~/utils/presentRoomType';
import { reportError } from '~/utils/reportError';

import { DeleteButton } from '../DeleteButton';
import ErrorDisplay from '../ErrorDisplay';

import AmenityForm from './AmenityForm';
import CopyRoomTypeAttributeForm from './CopyRoomTypeAttributeForm';
import ImagesForm from './ImagesForm';
import RoomImagesSyncImportBar from './RoomImagesSyncImportBar';
import Amenities from './fields/Amenities';
import { buttonMessages, buttonStates } from './states/submitButton';
import AIGeneratorTextAreaWidget from './widgets/AIGeneratorTextAreaWidget';

const fields = { amenities: Amenities };

// failedImageIds lets a retry skip already-succeeded imports instead of duplicating them.
class RoomImageImportError extends Error {
  constructor(message, failedImageIds) {
    super(message);
    this.failedImageIds = failedImageIds;
  }
}

const buildDescription = (conf) => {
  if (!conf.quantity || !conf.type) {
    return null;
  }

  const typeWithoutBed = conf.type.replace('Bed', '');
  const formattedType = typeWithoutBed
    .replace('DoubleSofa', 'Double Sofa')
    .replace('QueenSofa', 'Queen Sofa')
    .replace('RollAway', 'Roll Away');

  return `${conf.quantity} ${formattedType} Bed${conf.quantity > 1 ? 's' : ''}`;
};

const errorMessage = `This room type is currently attached to an offer. You must
  detach it from any offers before you can delete.`;
class RoomTypeForm extends Component {
  constructor(props) {
    super(props);

    const { offers } = props;

    this.vendorId = props.vendorId;
    this.property = props.property;

    this.state = {
      saveState: buttonStates.default,
      deleteState: 'delete',
      isUploadingPending: false,
      pendingUploadCount: 0,
      showingAmenitiesModal: false,
      isGeneratingBeddingConfiguration: false,
      isGeneratingRoomAmenities: false,
      isSyncingRoomImages: false,
      isFlashOffer: OFFER_TYPE_MAP[offers?.[0]?.type] ?? false,
    };

    this.imagesFormRef = createRef();
    this.roomTypeRefreshSequence = 0;

    this.handleChange = this.handleChange.bind(this);
    this.handleDeleteRoomType = this.handleDeleteRoomType.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleToggleAmenitiesModal = this.handleToggleAmenitiesModal.bind(this);
    this.handleSyncBeddingConfiguration = this.handleSyncBeddingConfiguration.bind(this);
    this.handleSyncRoomAmenities = this.handleSyncRoomAmenities.bind(this);
    this.handleSyncRoomImages = this.handleSyncRoomImages.bind(this);
    this.importRoomImages = this.importRoomImages.bind(this);
    this.getFormPayload = this.getFormPayload.bind(this);
    this.getPossibleAmenityOptions = this.getPossibleAmenityOptions.bind(this);
    this.onAddImage = this.onAddImage.bind(this);
    this.onUpdateImages = this.onUpdateImages.bind(this);
    this.onDeleteImage = this.onDeleteImage.bind(this);
    this.handlePendingUploadStateChange = this.handlePendingUploadStateChange.bind(this);

    this.schema = props.schema;

    this.schema.properties.bed_groups.items.properties = {
      id: this.schema.properties.bed_groups.items.properties.id,
      description: {
        type: 'string',
      },
      configuration: this.schema.properties.bed_groups.items.properties.configuration,
    };

    this.uiSchema = {
      property_id: { 'ui:widget': 'hidden' },
      amenities: { 'ui:field': 'amenities' },
      description: {
        'ui:widget': AIGeneratorTextAreaWidget,
        'ui:generatorContext': {
          propertyName: props.property.name,
          roomName: props.roomType.name,
          amenities: props.roomType.amenities,
          model: 'room-type',
          field: 'description',
        },
      },
      bed_groups: {
        'ui:options': {
          removable: true,
          orderable: true,
          addable: true,
        },
        items: {
          id: { 'ui:widget': 'hidden' },
          description: { 'ui:disabled': true },
          configuration: {
            'ui:options': {
              removable: true,
              orderable: true,
              addable: true,
            },
          },
        },
      },
    };
  }

  componentDidUpdate(prevProps) {
    const { saveAll, roomType } = this.props;
    if (saveAll !== prevProps.saveAll) {
      this.handleSubmit({
        formData: {
          ...roomType,
        },
      });
    }
  }

  isUniqueStays() {
    const { roomType } = this.props;
    return categories.isUniqueStay(roomType.category);
  }

  // getFormPayload returns a payload ready to be sent to the server for either
  // updating or creating a room type
  getFormPayload(form) {
    const ret = Object.assign({}, form.formData);

    // _id is used internally to identify forms
    delete ret._id;

    ret.bed_groups.forEach((x) => {
      delete x.description;
      (x.configuration || []).forEach((n) => {
        delete n.size;
      });
    });

    return ret;
  }

  // getPossibleAmenityOptions returns an array of all possible options for
  // amenities. Concretely, it combines the built-in amenity options returned by
  // the API and any custom amenity option that the room type has.
  getPossibleAmenityOptions() {
    const { amenityOptions, roomType } = this.props;

    // first, duplicate amenityOptions array
    const ret = amenityOptions.slice();
    roomType.amenities.forEach((amenity) => {
      if (ret.indexOf(amenity) === -1) {
        ret.push(amenity);
      }
    });

    return ret.sort();
  }

  handlePendingUploadStateChange({ isUploadingPending, pendingUploadCount }) {
    this.setState({
      isUploadingPending,
      pendingUploadCount,
    });
  }

  async handleSubmit(form) {
    const { roomType, onEditRoomType, onSaveCompleted, onSaveFailed } = this.props;
    this.setState({ saveState: buttonStates.saving, roomType: form.formData });

    const uploadResult = await this.imagesFormRef.current?.uploadAllPending?.();

    if (uploadResult?.failedCount) {
      this.setState({ saveState: buttonStates.failed });
      onSaveFailed();
      return;
    }

    let submitType = 'updateRoomType';

    if (!roomType.id) {
      submitType = 'createRoomType';
    }

    const propertyId = roomType.property_id;
    const roomTypeId = roomType.id;
    const payload = this.getFormPayload(form);
    delete payload.amenities_ordering; // this is not a field we want to send to the server for non-rentals now

    ReservationService[submitType](payload, propertyId, roomTypeId)
      .then((response) => {
        const updatedRoomType = Object.assign({}, presentRoomType(response.result), {
          _id: roomType._id,
        });

        this.setState({
          roomType: updatedRoomType,
          saveState: buttonStates.saved,
        });

        onEditRoomType(updatedRoomType);
        onSaveCompleted();
      })
      .catch((e) => {
        onSaveFailed();
        this.setState({ saveState: buttonStates.failed });
        reportError(e);
      });
  }

  async generateBeddingConfiguration(packageId) {
    const { enqueueSnackbar, roomType } = this.props;
    this.setState({ isGeneratingBeddingConfiguration: true });

    try {
      const response = await OffersService.generateBeddingConfiguration(packageId);

      const bedGroups = response.result.bed_grouping.map((y) => {
        const configuration = y.map((z) => ({
          quantity: z.quantity,
          type: z.bed_type,
        }));
        return {
          configuration: configuration,
        };
      });

      this.handleChange({
        formData: {
          ...roomType,
          bed_groups: bedGroups,
        },
      });

      enqueueSnackbar('Bedding configuration generated successfully for ' + roomType.name, { variant: 'success' });
    } catch (e) {
      enqueueSnackbar('Failed to generate bedding configuration for ' + roomType.name + ': ' + e.message, {
        variant: 'error',
      });
      reportError(e);
    } finally {
      this.setState({ isGeneratingBeddingConfiguration: false });
    }
  }

  handleChange(edit) {
    const { onEditRoomType } = this.props;
    const { saveState } = this.state;
    const formData = JSON.parse(JSON.stringify(edit.formData));

    const updatesBGData = (formData.bed_groups || []).map((x) => {
      const descriptionArray = (x.configuration || []).map((conf) => buildDescription(conf)).filter(Boolean);
      const lastItem = descriptionArray.length > 1 ? descriptionArray.pop() : null;

      return {
        ...x,
        description: `${descriptionArray.join(', ')}${lastItem ? ` and ${lastItem}` : ''}`,
      };
    });

    formData.bed_groups = updatesBGData;

    if (formData.bed_groups.length > 0) {
      formData.number_of_beds = formData.bed_groups.map((x) =>
        (x.configuration || []).reduce((acc, conf) => acc + (conf.quantity || 0), 0),
      );
    }

    onEditRoomType(formData);

    if (saveState !== buttonStates.default) {
      this.setState({
        saveState: buttonStates.default,
      });
    }
  }

  async handleDeleteRoomType() {
    const { roomType, onRemoveRoomType, formKey } = this.props;
    this.setState({ deleteState: 'deleting' });

    const roomTypeInUse = await this.findUsage();

    if (roomTypeInUse) {
      this.setState({ deleteState: 'failed' });
      return false;
    }

    ReservationService.deleteRoomType(roomType.property_id, roomType.id)
      .then(() => {
        onRemoveRoomType(formKey);
      })
      .catch(function (e) {
        reportError(e);
      });
    this.setState({ deleteState: 'delete' });
  }

  handleToggleAmenitiesModal(nextState) {
    this.setState((prevState) => {
      if (typeof nextState !== 'undefined') {
        return { showingAmenitiesModal: nextState };
      }

      return { showingAmenitiesModal: prevState.showingAmenitiesModal };
    });
  }

  handleSyncBeddingConfiguration() {
    const { roomType, offers, enqueueSnackbar } = this.props;

    const matchingPackages = offers.flatMap((offer) =>
      offer.packages.filter((pkg) => pkg.fk_room_type_id === roomType.id),
    );

    if (matchingPackages.length > 0) {
      const packageId = matchingPackages[0].id_salesforce_external;
      this.generateBeddingConfiguration(packageId);
    } else {
      enqueueSnackbar('No matching package found for this room type', { variant: 'warning' });
    }
  }

  async handleSyncRoomAmenities() {
    const { roomType, enqueueSnackbar } = this.props;

    if (!roomType.id) {
      enqueueSnackbar('Room type must be saved before syncing amenities', { variant: 'warning' });
      return;
    }

    this.setState({ isGeneratingRoomAmenities: true });

    try {
      const response = await syncRoomAmenities({
        reservationPropertyId: roomType.property_id,
        reservationRoomId: roomType.id,
      });

      const syncedAmenities = (response.result?.amenities || []).map((amenity) => amenity.name);

      if (syncedAmenities.length === 0) {
        enqueueSnackbar(response.result?.message, { variant: 'warning' });
        return;
      }

      this.handleChange({
        formData: {
          ...roomType,
          amenities: syncedAmenities,
        },
      });

      enqueueSnackbar(`Successfully synced ${syncedAmenities.length} amenities for ${roomType.name}`, {
        variant: 'success',
      });
    } catch (e) {
      enqueueSnackbar('Failed to sync room amenities: ' + e.message, {
        variant: 'error',
      });
      reportError(e);
    } finally {
      this.setState({ isGeneratingRoomAmenities: false });
    }
  }

  onAddImage(cloudinaryId) {
    const { roomType } = this.props;
    return ReservationService.createRoomTypeImage(
      { id_cloudinary_external: cloudinaryId },
      roomType.property_id,
      roomType.id,
    );
  }

  async refreshRoomType() {
    const { roomType } = this.props;
    const sequence = ++this.roomTypeRefreshSequence;

    const updatedRoomType = await ReservationService.getRoomType(roomType.property_id, roomType.id);

    if (sequence !== this.roomTypeRefreshSequence) {
      return;
    }

    const { roomType: currentRoomType, onEditRoomType } = this.props;
    onEditRoomType({ ...currentRoomType, images: presentRoomType(updatedRoomType.result).images });
  }

  async onUpdateImages(newImageList) {
    const { roomType, enqueueSnackbar } = this.props;
    const updatePayload = newImageList.map((image) => ({
      id: image.id,
      id_cloudinary_external: image.id_cloudinary_external,
      order: image.order,
      title: image.title,
      hidden: image.hidden ?? false,
    }));

    await ReservationService.updateRoomTypeImages(updatePayload, roomType.property_id, roomType.id);

    try {
      await this.refreshRoomType();
    } catch (e) {
      // The save itself succeeded; a failed refresh shouldn't surface as a save failure.
      enqueueSnackbar('Images saved, but the room type view could not be refreshed. Reload to see the latest.', {
        variant: 'warning',
      });
      reportError(e);
    }
  }

  onDeleteImage(imageId) {
    const { roomType } = this.props;
    return ReservationService.deleteRoomTypeImage(imageId, roomType.property_id, roomType.id);
  }

  async importRoomImages(imageIds) {
    const { roomType, enqueueSnackbar } = this.props;

    const results = await allSettledWithConcurrency(imageIds, 1, (imageId) => this.onAddImage(imageId));
    const failedImageIds = imageIds.filter((_, index) => results[index].status === 'rejected');
    const failedCount = failedImageIds.length;
    const importedCount = results.length - failedCount;

    if (importedCount > 0) {
      try {
        await this.refreshRoomType();
      } catch (e) {
        enqueueSnackbar('Images imported, but the room type view could not be refreshed. Reload to see the latest.', {
          variant: 'warning',
        });
        reportError(e);
      }
    }

    if (failedCount > 0) {
      enqueueSnackbar(`Imported ${importedCount} image(s), ${failedCount} failed`, { variant: 'warning' });
      throw new RoomImageImportError(`${failedCount} image(s) failed to import`, failedImageIds);
    }

    if (importedCount > 0) {
      enqueueSnackbar(`Imported ${importedCount} image(s) for ${roomType.name}`, { variant: 'success' });
    }
  }

  async handleSyncRoomImages() {
    const { roomType, enqueueSnackbar } = this.props;

    if (!roomType.id) {
      enqueueSnackbar('Room type must be saved before syncing images', { variant: 'warning' });
      return;
    }

    this.setState({ isSyncingRoomImages: true });

    try {
      const remainingSlots = Math.max(
        0,
        SYNC_TOP_ROOM_IMAGE_COUNT - roomType.images.filter((image) => !image.hidden).length,
      );

      if (remainingSlots <= 0) {
        enqueueSnackbar(`Already showing ${SYNC_TOP_ROOM_IMAGE_COUNT} images for this room`, { variant: 'info' });
        return;
      }

      const response = await getRoomImagesByInternalService({
        internalService: 'reservation',
        internalServiceId: roomType.id,
        limit: SYNC_ROOM_FETCH_LIMIT,
      });
      const images = response.result?.images ?? [];

      const existingPublicIds = new Set(roomType.images.map((image) => image.id_cloudinary_external));
      const duplicateImageIds = resolveCanonicalRootIds(images, (image) => existingPublicIds.has(image.imageId));
      const newImages = images
        .filter((image) => !existingPublicIds.has(image.imageId) && !duplicateImageIds.has(image.id))
        .slice(0, remainingSlots);

      if (newImages.length === 0) {
        enqueueSnackbar('No new images to sync for this room', { variant: 'info' });
        return;
      }

      await this.importRoomImages(newImages.map((image) => image.imageId));
    } catch (e) {
      if (isPropertyNotMappedError(e)) {
        enqueueSnackbar(`Can't sync images: ${roomType.name} has no approved mapping in accommodation`, {
          variant: 'error',
        });
        return;
      }

      if (!(e instanceof RoomImageImportError)) {
        enqueueSnackbar('Failed to sync room images: ' + e.message, { variant: 'error' });
        reportError(e);
      }
    } finally {
      this.setState({ isSyncingRoomImages: false });
    }
  }

  findUsage = async () => {
    const { roomType } = this.props;
    const { properties, offers } = await this.fetchData();

    const relatedRoomTypes = properties.reduce((acc, property) => {
      property?.room_types?.forEach((rt) => {
        if (rt?.id === roomType.id) {
          acc.add(rt.id);
        }
      });
      return acc;
    }, new Set());

    const typeOffers = offers.filter((offer) => {
      return offer.packages.some(
        (p) =>
          relatedRoomTypes.has(p.fk_room_type_id) ||
          p.package_options?.some((o) => relatedRoomTypes.has(o.fk_room_type_id)),
      );
    });

    return typeOffers.length;
  };

  async fetchData() {
    const [properties, offers] = await Promise.all([
      ReservationService.getProperties(this.vendorId),
      OffersService.getOffers({
        queryString: this.vendorId,
      }),
    ]).catch(function (e) {
      reportError(e);
    });

    return {
      properties: properties.result,
      offers: offers.result,
    };
  }

  getRoomTypes() {
    const { roomTypes } = this.props;
    return roomTypes || [];
  }

  render() {
    const { onAddAmenityOption, roomType, channelManager, schema, roomTypeNames, onCopyAttributes } = this.props;

    if (!roomType) {
      return null;
    }

    const {
      showingAmenitiesModal,
      isGeneratingBeddingConfiguration,
      isSyncingRoomImages,
      deleteState,
      saveState,
      isFlashOffer,
      isUploadingPending,
      pendingUploadCount,
    } = this.state;

    const allAmenityOptions = this.getPossibleAmenityOptions();

    const { isGeneratingRoomAmenities } = this.state;

    // Pass data to custom fields
    const formContext = {
      amenityOptions: allAmenityOptions,
      onStartAddingAmenities: this.handleToggleAmenitiesModal.bind(this, true),
      onSyncRoomAmenities: this.handleSyncRoomAmenities,
      isGeneratingRoomAmenities,
      isFlashOffer,
    };

    if (!roomType.size_sqm) {
      delete roomType.size_sqm;
    }

    if (!roomType.availability_threshold) {
      delete roomType.availability_threshold;
    }

    if (!roomType.category) {
      roomType.category = this.property.category;
    }

    this.uiSchema.room_type_code = {
      'ui:widget': channelManager !== PROPERTY_CHANNEL_MANAGERS.selfManaged ? 'text' : 'hidden',
    };

    const isUniqueStays = this.isUniqueStays();
    this.schema.properties.number_of_bedrooms.minimum = isUniqueStays ? 1 : 0;

    this.uiSchema.number_of_bedrooms = { 'ui:widget': 'text' };
    if (isUniqueStays) {
      if (!this.schema.required.includes('number_of_bedrooms')) {
        this.schema.required = [...schema.required, 'number_of_bedrooms'];
      }
    } else {
      this.schema.required = this.schema.required.filter((key) => key !== 'number_of_bedrooms');
    }

    const images = roomType.images.map((i) => ({
      ...i,
      publicImageId: i.id_cloudinary_external,
    }));

    // RJSF v6: Remove amenities_ordering from schema as it's managed in a separate tab
    delete this.schema.properties.amenities_ordering;

    const contentEnabled = featureToggle.availableToShow('ADDITIONAL_PROPERTY_CONTENT_ENABLED');
    if (!contentEnabled) {
      delete this.schema.properties.room_policies;

      this.uiSchema.room_policies = {
        'ui:widget': 'hidden',
      };
    }

    return (
      <Box>
        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={2} mx={2}>
          <Stack direction="row" gap={2} alignItems="center">
            {isFlashOffer && (
              <Button
                variant="outlined"
                onClick={this.handleSyncBeddingConfiguration}
                disabled={isGeneratingBeddingConfiguration}
                startIcon={isGeneratingBeddingConfiguration ? <Spinner size={16} /> : null}
              >
                {isGeneratingBeddingConfiguration ? 'Syncing...' : 'Sync Bedding Configuration'}
              </Button>
            )}
            <CopyRoomTypeAttributeForm
              schemaProperties={schema.properties}
              roomTypeNames={roomTypeNames}
              roomTypeId={roomType._id}
              onCopyAttributes={onCopyAttributes}
            />
          </Stack>
        </Box>
        <Form
          schema={this.schema}
          uiSchema={{ ...this.uiSchema }}
          fields={fields}
          formData={roomType}
          onChange={this.handleChange}
          onSubmit={this.handleSubmit}
          formContext={formContext}
          validator={validator}
          transformErrors={(errors) =>
            // Temporarily ignore additionalProperties to allow client side validation to pass.
            // The formdata is cleaned in handleChange and handleSubmit.
            errors.filter(
              (error) =>
                !(
                  error.name === 'additionalProperties' &&
                  error.property?.match(/^\.bed_groups\.\d+\.configuration\.\d+$/)
                ),
            )
          }
        >
          <ImagesForm
            ref={this.imagesFormRef}
            enablePendingCropUpload
            images={images}
            onAddImage={this.onAddImage}
            onUpdateImages={this.onUpdateImages}
            onDeleteImage={this.onDeleteImage}
            onPendingUploadStateChange={this.handlePendingUploadStateChange}
            hasHiddenToggle
            headerActions={
              <RoomImagesSyncImportBar
                roomId={roomType.id}
                roomName={roomType.name || 'this room'}
                existingImageIds={images.map((image) => image.id_cloudinary_external)}
                isSyncing={isSyncingRoomImages}
                onSync={this.handleSyncRoomImages}
                onImport={this.importRoomImages}
              />
            }
          />

          {deleteState == 'failed' ? <ErrorDisplay message={errorMessage} /> : null}

          <Box className="button-container">
            <Stack direction="row" spacing={2}>
              {pendingUploadCount > 1 ? (
                <Button
                  variant="contained"
                  disabled={isUploadingPending}
                  onClick={() => this.imagesFormRef.current?.uploadAllPending?.()}
                >
                  Upload all images
                </Button>
              ) : null}
              <Button variant="contained" type="submit" disabled={saveState === buttonStates.saving}>
                {buttonMessages[saveState]}
              </Button>
              {roomType.id ? <DeleteButton onClick={this.handleDeleteRoomType} buttonText={deleteState} /> : null}
            </Stack>
          </Box>
        </Form>

        <Dialog
          open={showingAmenitiesModal}
          onClose={() => this.handleToggleAmenitiesModal(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle id="form-dialog-title">Add a new amenity type</DialogTitle>
          <DialogContent>
            <AmenityForm
              onSubmit={onAddAmenityOption}
              onCancel={() => this.handleToggleAmenitiesModal(false)}
              onAfterSubmit={() => this.handleToggleAmenitiesModal(false)}
            />
          </DialogContent>
        </Dialog>
      </Box>
    );
  }
}

export default withSnackbar(RoomTypeForm);
